/**
 * Quick deposit / withdraw container (C19, customer mode C22): binds `QuickTxModal` (packages/ui, App.tsx:54572-55592)
 * to the gateway (D04 §3, §5; D-077 only digital boxes; D-074 digital commission):
 * every figure of the summary column is the server's — a debounced `preview` runs on each change and
 * its `derived` strings (cash commission, box after) fill the summary; a refusal becomes the
 * "insufficient balance" alert (BALANCE_INSUFFICIENT) or an inline error; "احتساب عمولة رقمية" asks the
 * preview for the auto-calculated commission and writes it into the field. Submit = preview → PIN →
 * commit (`useGateway`).
 *
 * [C22] Customer mode (D-097): the customer counterparty of `quick_deposit` / `quick_withdraw` on an IQD debt
 * ("ديون") or trust ("أمانات") account picked from the register; a name typed but not picked is checked for the same
 * name and opened inside the operation (old: created silently, D03 §4.5; now D-017 / D-018, `newAccounts`). Without
 * "partial breakdown" the whole customer side is on account (old deferred default); with it, the typed cash part
 * (the remaining field mirrors it, display arithmetic over decimal strings — the posted split is the engine's).
 * A decrease beyond the account's balance opens the old excess dialogs (App.tsx:48042-48095, D-218): one operation
 * with the engine's `surplus` option. The due date is posted with the date only (the engine keeps `dueDate`; the
 * alert's time of day belongs to the D-049 notification rules, not yet built).
 */
import { useEffect, useRef, useState } from "react";
import { QuickTxModal, formatWithCommas, type QuickTxFormValues, type QuickTxMode, type QuickTxSummary, type QuickTxType } from "@faylasuf/ui";
import type { AccountRow, PreviewResult } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { customerPinMessage, decClampSub, surplusPrimary, useExcessFlow, useNewCustomerName, type CustomerTarget, type ExcessResolution } from "../../app/customer-picker.js";
import { engineErrors, errorMessage, isSessionLost } from "../../app/errors.js";
import { useGateway } from "../../app/operations.js";
import type { SectionFlags } from "../../app/sections.js";
import { useToast } from "../../app/toast.js";
import { officeDate } from "./StatementContainer.js";

const EMPTY: QuickTxFormValues = {
  boxId: "",
  amount: "",
  cost: "",
  digitalCommission: "",
  note: "",
  customerId: "",
  customerName: "",
  allocation: "debt",
  partialBreakdown: false,
  cashReceived: "",
  remainingOnAccount: "",
  deferred: false,
  dueDate: "",
  dueHour: "",
  dueMinute: "",
  dueSecond: "",
};

function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function QuickTxContainer({ type, accounts, flags, defaultRatePct, onClose, onPosted }: { type: QuickTxType; accounts: readonly AccountRow[]; flags: SectionFlags; defaultRatePct: string; onClose: () => void; onPosted: (boxId: string) => void }) {
  const { t, now } = useApp();
  const { session, sessionLost } = useSession();
  const toast = useToast();
  const gateway = useGateway();
  const [mode, setMode] = useState<QuickTxMode>("box");
  const [values, setValues] = useState<QuickTxFormValues>(EMPTY);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [insufficient, setInsufficient] = useState<QuickTxSummary["insufficient"] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const names = useNewCustomerName(accounts);
  /** [C22] the new / same-name-resolved customer (reset when the customer fields change). */
  const target = useRef<CustomerTarget | null>(null);

  const boxes = accounts.filter((a) => a.kind === "digital_box" && !a.archived).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const vault = accounts.find((a) => a.kind === "cash_vault" && a.currency === "IQD");
  const myCustody = vault ? accounts.find((a) => a.kind === "custody" && a.parentAccountId === vault.id && a.ownerId === session.user.id) : undefined;
  const box = boxes.find((b) => b.id === values.boxId);
  const cashAvailable = flags.subSafesActive && myCustody ? myCustody.available : (vault?.available ?? "0");

  const isCustomer = mode === "customer";
  const kind = values.allocation === "debt" ? "debt" : "trust";
  const ledgerKind = kind === "debt" ? "customer_debt" : "customer_trust";
  const register = accounts.filter((a) => a.kind === ledgerKind && a.currency === "IQD" && !a.archived);
  const picked = register.find((a) => a.id === values.customerId);
  const typedName = values.customerName.trim();

  const patch = (p: Partial<QuickTxFormValues>) => {
    if ("customerName" in p || "customerId" in p || "allocation" in p) target.current = null;
    setValues((v) => {
      const next = { ...v, ...p };
      // old redistribution (:54917-54926, :55033-55076): the other field mirrors the typed one over the customer side
      const side = type === "deposit" ? next.amount : next.cost || next.amount;
      if ("cashReceived" in p && side) next.remainingOnAccount = p.cashReceived ? decClampSub(side, p.cashReceived) : "";
      else if ("remainingOnAccount" in p && side) next.cashReceived = p.remainingOnAccount ? decClampSub(side, p.remainingOnAccount) : "";
      return next;
    });
  };

  const dueDate = values.deferred || values.dueDate ? values.dueDate : "";
  const counterparty = (accountId: string, surplus?: ExcessResolution["surplus"]): Record<string, unknown> => ({
    kind: "customer",
    accountId,
    ...(values.partialBreakdown && values.cashReceived !== "" ? { cashReceived: values.cashReceived } : values.partialBreakdown && values.remainingOnAccount !== "" ? { remainingOnAccount: values.remainingOnAccount } : {}),
    ...(surplus ? { surplus } : {}),
    ...(dueDate ? { dueDate } : {}),
  });

  const input = (digital: string | undefined, cp?: Record<string, unknown>): Record<string, unknown> => ({
    amount: values.amount,
    ...(values.cost ? { cost: values.cost } : {}),
    ...(digital !== undefined && digital !== "" ? { digitalCommission: digital } : {}),
    boxAccountId: values.boxId,
    vaultAccountId: vault?.id ?? "",
    ...(cp ? { counterparty: cp } : {}),
    ...(values.note ? { note: values.note } : {}),
  });
  /** The dry run's counterparty: the picked account; an unpicked name is planned in box mode (the box side is the same). */
  const previewCp = isCustomer && picked ? counterparty(picked.id) : undefined;
  const previewKey = JSON.stringify(previewCp ?? null);

  // Debounced dry run: the summary shows only what the server planned (ADR-08 §2.1 item 2).
  useEffect(() => {
    setPreview(null);
    setInsufficient(undefined);
    setError(null);
    if (!values.boxId || !values.amount || !vault) return;
    const my = ++seq.current;
    const h = setTimeout(async () => {
      try {
        const pv = await gateway.preview(type === "deposit" ? "quick_deposit" : "quick_withdraw", input(isCustomer ? undefined : values.digitalCommission, previewCp));
        if (my === seq.current) setPreview(pv);
      } catch (e) {
        if (my !== seq.current) return;
        if (isSessionLost(e)) return sessionLost();
        const first = engineErrors(e)[0];
        if (first?.code === "BALANCE_INSUFFICIENT") {
          setInsufficient({
            costLabel: formatWithCommas(String(first.params["requested"] ?? values.cost ?? values.amount)),
            availableLabel: formatWithCommas(String(first.params["available"] ?? "0")),
            place: type === "deposit" ? "box" : flags.subSafesActive ? "sub_safe" : "vault",
          });
        } else if (first?.code !== "SURPLUS_ACCOUNT_REQUIRED") setError(errorMessage(t, e)); // the excess dialog opens on submit (old order)
      }
    }, 300);
    return () => clearTimeout(h);
  }, [values.boxId, values.amount, values.cost, values.digitalCommission, type, vault?.id, mode, previewKey]); // the preview depends on these inputs only

  const autoCommission = async () => {
    if (!values.boxId || !values.amount) return;
    try {
      const pv = await gateway.preview(type === "deposit" ? "quick_deposit" : "quick_withdraw", input(undefined));
      setValues((v) => ({ ...v, digitalCommission: pv.derived["digital_commission_iqd"] ?? "" }));
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else setError(errorMessage(t, e));
    }
  };

  const excess = useExcessFlow({
    accounts,
    // old App.tsx:48089-48095: amount and cost become the trust balance
    onReduce: (balance) => {
      setValues((v) => ({ ...v, amount: balance, cost: balance }));
      toast(t("excess.reduced_quick"), "info");
    },
    onResolved: (res) => void submit(res),
  });

  const submit = async (excessRes?: ExcessResolution) => {
    if (!values.boxId || !values.amount) return;
    const opType = type === "deposit" ? "quick_deposit" : "quick_withdraw";
    let message = t(type === "deposit" ? "pin.message_quick_deposit" : "pin.message_quick_withdraw", { amount: formatWithCommas(values.amount) });
    if (!isCustomer) {
      setBusy(true);
      try {
        const res = await gateway.run(opType, input(values.digitalCommission), { pinMessage: message, successKey: "toast.operation_posted" });
        if (res) {
          onPosted(values.boxId);
          onClose();
        }
      } catch {
        /* toasted */
      } finally {
        setBusy(false);
      }
      return;
    }
    // customer mode (D-097)
    if (!picked && !target.current) {
      if (!typedName) return setError(t("excess.select_account_first"));
      const resolved = await names.resolve(typedName, kind, "IQD", "c");
      if (!resolved) return;
      target.current = resolved;
    }
    const accountId = picked?.id ?? target.current?.accountId ?? "";
    const newAccounts = [...(!picked ? (target.current?.newAccounts ?? []) : []), ...(excessRes?.newAccounts ?? [])];
    setBusy(true);
    let pv: PreviewResult;
    try {
      pv = await gateway.previewWithNew(opType, input(undefined, counterparty(accountId, excessRes?.surplus)), newAccounts);
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else {
        const sur = engineErrors(e).find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
        const primary = sur ? surplusPrimary(accounts, String(sur.params["account"] ?? ""), target.current, kind, "IQD") : undefined;
        if (sur && primary && !excessRes) excess.open({ account: primary, surplus: String(sur.params["surplus"] ?? "0") });
        else setError(errorMessage(t, e));
      }
      setBusy(false);
      return;
    }
    try {
      const opened = [...(!picked && target.current?.newAccount ? [target.current.newAccount] : []), ...(excessRes?.newAccount ? [excessRes.newAccount] : [])];
      message = customerPinMessage(t, message, { newAccounts: opened, ...(excessRes ? { excess: { amount: pv.derived["surplus_iqd"] ?? "0", currency: "د.ع", target: excessRes.targetLabel } } : {}) });
      const res = await gateway.commitPreview(pv, { pinMessage: message, successKey: "toast.operation_posted" });
      if (res) {
        target.current = null;
        onPosted(values.boxId);
        onClose();
      }
    } catch {
      /* toasted */
    } finally {
      setBusy(false);
    }
  };

  const today = officeDate(now(), session.office.timeZone);
  const rate = (type === "deposit" ? box?.depositRatePct : box?.withdrawRatePct) ?? defaultRatePct;
  const summary: QuickTxSummary = {
    balanceSource: type === "deposit" ? "box" : "vault",
    balanceLabel: formatWithCommas(type === "deposit" ? (box?.available ?? "0") : cashAvailable),
    ...(values.amount ? { amountLabel: formatWithCommas(values.amount) } : {}),
    cashCommissionLabel: formatWithCommas(preview?.derived["cash_commission_iqd"] ?? "0"),
    digitalCommissionRateLabel: rate,
    ...(insufficient ? { insufficient } : {}),
    ...(isCustomer && picked ? { customerBalanceLabel: `(${formatWithCommas(picked.balance)})` } : {}),
    ...(isCustomer && values.dueDate ? { dueAtLabel: `${values.dueDate} ${values.dueHour || "12"}:${values.dueMinute || "00"}:${values.dueSecond || "00"}` } : {}),
  };

  return (
    <>
      <QuickTxModal
        open
        type={type}
        mode={mode}
        onModeChange={(m) => {
          target.current = null;
          setMode(m);
        }}
        values={values}
        onChange={patch}
        boxOptions={boxes.map((b) => ({ id: b.id, label: b.name ?? "", balanceLabel: formatWithCommas(b.balance) }))}
        customerOptions={register.map((a) => ({ id: a.id, label: a.name ?? "", balanceLabel: `(${formatWithCommas(a.balance)} د.ع)` }))}
        summary={summary}
        onAutoCommission={() => void autoCommission()}
        onSubmit={() => void submit()}
        onClose={onClose}
        onDueDateShortcut={(days) => patch({ dueDate: addDays(today, days), deferred: true })}
        onDueTimeNow={() => {
          const d = now();
          patch({ dueDate: values.dueDate || today, dueHour: String(d.getHours()).padStart(2, "0"), dueMinute: String(d.getMinutes()).padStart(2, "0"), dueSecond: String(d.getSeconds()).padStart(2, "0") });
        }}
        busy={busy}
        error={error}
      />
      {excess.dialog}
      {names.dialog}
      {gateway.pin.dialog}
    </>
  );
}
